import { withTransientRetry } from "@/pages/home/hooks/use-presentation-generation-flow";
import { presentationGenerationApi } from "@/shared/services/presentation-generation/presentation-generation.api";
import axios from "axios";
import { useEffect } from "react";
import type { HydratePayload } from "../stores/file-to-storydoc-generation.store";
import {
  type ImportEntry,
  useImportRegistry,
} from "../stores/import-registry.store";
import { loadImportSessions } from "../utils/import-session-storage";
import { trackImport } from "../utils/track-import";

const resumeImport = async (
  { id, store }: ImportEntry,
  persisted: HydratePayload,
) => {
  const jobId = persisted.jobId as string;
  // idle (jobId kept) until the backend confirms the job; toasts appear after
  store.getState().hydrate({ ...persisted, status: "idle" });
  const runId = store.getState().runId;
  const isStale = () =>
    !useImportRegistry.getState().imports.some((i) => i.id === id) ||
    store.getState().jobId !== jobId ||
    store.getState().runId !== runId;
  const drop = () => useImportRegistry.getState().removeImport(id);
  const controller = new AbortController();
  const { signal } = controller;
  const unsubscribe = useImportRegistry.subscribe((s) => {
    if (!s.imports.some((i) => i.id === id)) controller.abort();
  });
  try {
    const st = await withTransientRetry(
      () => presentationGenerationApi.getJobStatus(jobId, signal),
      signal,
    );
    if (isStale()) return;
    if (st.status === "failed") return drop();
    if (st.status === "done") {
      const res = await withTransientRetry(
        () => presentationGenerationApi.getJobResult(jobId, signal),
        signal,
      );
      if (isStale()) return;
      if (!res.result) return drop();
      store.getState().applyResult(res.result);
      trackImport("resumed", { via: "reload", status: "done" }, store);
      return;
    }
    store.getState().setStatus("generating");
    trackImport("resumed", { via: "reload", status: "running" }, store);
  } catch (e) {
    if (isStale()) return;
    const code = axios.isAxiosError(e) ? e.response?.status : undefined;
    if (code === 404 || code === 410) drop();
    // otherwise keep the idle entry (jobId kept) so the next load retries
  } finally {
    unsubscribe();
  }
};

export const useResumeImports = (userId: string | null) => {
  useEffect(() => {
    if (!userId) return;
    const registry = useImportRegistry.getState();
    if (registry.ownerId !== userId) return;
    const registered = new Set(
      registry.imports.map((i) => i.store.getState().jobId),
    );
    for (const persisted of loadImportSessions(userId)) {
      if (!persisted.jobId || registered.has(persisted.jobId)) continue;
      const entry = useImportRegistry.getState().addImport();
      if (!entry) break;
      void resumeImport(entry, persisted);
    }
  }, [userId]);
};
